import { DataType } from '@mode-switch/express';
import { ReportFilterControlHelpers, ReportFilterFieldFactory, ReportFilterFieldTypes } from '@mode/filters/util';
import { AccountResource, ReportResource, SpaceHelpers, StarResource } from '@mode/legacy/data-access-model-js';
import { Resource } from '@mode/shared/util-halogen';
import {
  AppState,
  ClientState,
  ImmutableRecordSet,
  ModelState,
  QueryRunState,
  QueryState,
  ReportDataset,
  ReportRunState,
  SelectedReportState,
  TransientState,
} from '@mode/legacy/types-app-state';
import { fromReportFilterSelectors } from '@mode/report-viewer/domain';
import { headerReportData } from '@mode/reports/domain';
import { ReportTypes } from '@mode/shared/contract-common';
import { CompiledAttr } from '@mode/shared/util-switch';
import { fromSwitchViewSelectors } from '@mode/switch-views/domain';
import { Map as ImmMap } from 'immutable';
import { get, mapKeys, sortBy } from 'lodash';
import { createSelector } from 'reselect';
import { selectedDefinitionQueryResource } from '~/modules/definitions/domain';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';

export function selectState<T extends keyof AppState>(root: AppState, p1: T): AppState[T];
export function selectState<T1 extends keyof AppState, T2 extends keyof AppState[T1]>(
  root: AppState,
  p1: T1,
  p2: T2
): AppState[T1][T2];
export function selectState<
  T1 extends keyof AppState,
  T2 extends keyof AppState[T1],
  T3 extends keyof AppState[T1][T2],
>(root: AppState, p1: T1, p2: T2, p3: T3): AppState[T1][T2][T3];
export function selectState<
  T1 extends keyof AppState,
  T2 extends keyof AppState[T1],
  T3 extends keyof AppState[T1][T2],
  T4 extends keyof AppState[T1][T2][T3],
>(root: AppState, p1: T1, p2: T2, p3: T3, p4: T4): AppState[T1][T2][T3][T4];
export function selectState<
  T1 extends keyof AppState,
  T2 extends keyof AppState[T1],
  T3 extends keyof AppState[T1][T2],
  T4 extends keyof AppState[T1][T2][T3],
  T5 extends keyof AppState[T1][T2][T3][T4],
>(root: AppState, p1: T1, p2: T2, p3: T3, p4: T4, p5: T5): AppState[T1][T2][T3][T4][T5];
export function selectState(root: AppState, ...paths: string[]) {
  return get(root, paths);
}

// Client
export const clientSelector = (state: AppState) => state.client;

export const loggedInStateSelector = (state: AppState) => state.client.loggedIn;

export const expandedViewOpenSelector = (state: AppState) => state.client.expandedViewOpen;

export const currentReportTokenSelector = (state: AppState) => state.client.currentReportToken;

export const currentAccountUsernameSelector = (state: AppState) => state.client.currentAccountUsername;

export const currentUserBelongsToReportContextSelector = (
  state: AppState
): ClientState['currentUserBelongsToReportContext'] => state.client.currentUserBelongsToReportContext;

export const selectedReportSelector = (state: AppState): SelectedReportState => state.client.selectedReport;

export const selectedReportPythonNotebookLinkSelector = (state: AppState): string =>
  state.client.selectedReport.report &&
  state.client.selectedReport.report.hasLink('python_notebook') &&
  state.client.selectedReport.report.links('python_notebook').href;

export const selectedReportWebLinkSelector = (state: AppState): string =>
  state.client.selectedReport.report &&
  state.client.selectedReport.report.hasLink('web') &&
  state.client.selectedReport.report.links('web').href;

export const selectedReportWebEditLinkSelector = (state: AppState): string =>
  state.client.selectedReport.report &&
  state.client.selectedReport.report.hasLink('web_edit') &&
  state.client.selectedReport.report.links('web_edit').href;

export const selectedReportIndexViewLinkSelector = (state: AppState): string =>
  state.client.selectedReport.report &&
  state.client.selectedReport.report.hasLink('report_index_web') &&
  state.client.selectedReport.report.links('report_index_web').href;

export const selectedReportQueryCountSelector = (state: AppState): number =>
  state.client.selectedReport.report.query_count;

export const selectedReportResourceSelector = (state: AppState) => state.client.selectedReport.report;

export const selectedReportIsNewSelector = (state: AppState) => state.client.selectedReport.reportIsNew;

export const selectedReportReportRunStateSelector = (state: AppState) => state.client.selectedReport.reportRun;

export const selectedReportReportRunResourceSelector = (state: AppState) =>
  state.client.selectedReport.reportRun && state.client.selectedReport.reportRun.resource;

export const selectedReportReportRunPdfExportSelector = (state: AppState) =>
  state.client.selectedReport.reportRunPdfExport;

export const selectedReportLinks = (state: AppState) =>
  state.client.selectedReport.report && state.client.selectedReport.report['_links'];

export const selectedReportRunCreatedAtSelector = (state: AppState): ReportRunState['createdAt'] =>
  state.client.selectedReport.reportRun && state.client.selectedReport.reportRun.createdAt;

export const selectedReportExpectedRuntimeSelector = (state: AppState): ReportResource['expected_runtime'] =>
  state.client.selectedReport.report && state.client.selectedReport.report.expected_runtime;

export const selectedReportShowParamsHeader = (state: AppState): boolean =>
  state.client.selectedReport.reportRun &&
  state.client.selectedReport.reportRun.resource &&
  state.client.selectedReport.reportRun.resource.form_fields.length > 0;

export const selectedReportShowFilterHeader = (state: AppState): boolean =>
  state.client.selectedReport.report &&
  state.client.selectedReport.report._embedded['display_properties'].show_filter_header;

export const selectedReportLayoutSelector = (state: AppState): SelectedReportState['reportLayout'] =>
  state.client.selectedReport.reportLayout;

export const selectedReportLastSuccessfulRunTokenSelector = (
  state: AppState
): ReportResource['last_successful_run_token'] =>
  state.client.selectedReport.report && state.client.selectedReport.report.last_successful_run_token;

export const selectedReportWebReportRunsLinkSelector = (state: AppState): string =>
  state.client.selectedReport.report &&
  state.client.selectedReport.report.hasLink('web_report_runs') &&
  state.client.selectedReport.report.links('web_report_runs').href;

export const selectedReportCreatorSelector = (state: AppState): AccountResource => state.client.selectedReport.creator;

export const selectedReportCreatorEmailSelector = (state: AppState): string =>
  state.client.selectedReport.creator.email;

export const selectedReportUserStarSelector = (state: AppState): StarResource => state.client.selectedReport.userStar;

export const selectedReportModelsSelector = (state: AppState): ImmutableRecordSet<ModelState> =>
  state.client.selectedReport.chartModels;

export const accountContextSelector = (state: AppState): ClientState['accountContext'] => state.client.accountContext;

export const accountContextPlanCode = (state: AppState): AccountResource['plan_code'] =>
  state.client.accountContext && state.client.accountContext.plan_code;

export const accountContextSpacesSelector = (state: AppState): ClientState['accountContextSpaces'] =>
  state.client.accountContextSpaces;

export const reportContextSelector = (state: AppState): ClientState['reportContext'] => state.client.reportContext;

// not complete, returns the query parameters
export const getReportBuilderUrl = createSelector(
  reportContextSelector,
  (reportOwner) => `?report_owner_username=${reportOwner.username}&report_owner_plan=${reportOwner.plan_code}`
);

export const reportContextNameSelector = (state: AppState): string =>
  state.client.reportContext && state.client.reportContext.name;

export const reportContextWebLinkSelector = (state: AppState): string =>
  state.client.reportContext &&
  state.client.reportContext.hasLink('web') &&
  state.client.reportContext.links('web').href;

export const reportContextAvatarSelector = (state: AppState): ClientState['reportContextAvatar'] =>
  state.client.reportContextAvatar;

export const reportContextUsernameSelector = (state: AppState): string =>
  state.client.reportContext && state.client.reportContext.username;

export const reportContextFeaturesSelector = (state: AppState): ClientState['reportContextFeatures'] =>
  state.client.reportContextFeatures;

export const accountSelector = (state: AppState): ClientState['account'] => state.client.account;

export const accountAvatarSelector = (state: AppState): ClientState['account'] => state.client.account?.avatar;

export const accountEmailSelector = (state: AppState): string => state.client.account.email;

export const accountOrganizationsSelector = (state: AppState): ClientState['accountOrganizations'] =>
  state.client.accountOrganizations;

export const accountPreferenceSelector = (state: AppState): TransientState['accountPreference'] =>
  state.transient.accountPreference;

export const reportFilterSelector = (state: AppState): TransientState => state.transient;

export const selectedReportDisplayNameSelector = createSelector(headerReportData, (headerData) => {
  if (headerData?.name) {
    return headerData.name;
  }
  return headerData?.type === ReportTypes.ReportType.DatasetReport ? 'Untitled Dataset' : 'Untitled Report';
});

export const selectedReportSpaceSelector = createSelector(headerReportData, (headerData: any) => {
  const space = headerData?._embedded?.space;
  if (space && !(space instanceof Resource)) {
    return new Resource(space);
  }
  return space;
});

export const selectedReportSpaceWebLinkSelector = createSelector(headerReportData, (headerData: any) => {
  return headerData?._embedded?.space?._links?.['web']?.href;
});

export const selectedReportCollectionIsCommunity = createSelector(
  selectedReportSpaceSelector,
  SpaceHelpers.isCommunitySpace
);

export const selectedReportCollectionIsPersonal = createSelector(selectedReportSpaceSelector, SpaceHelpers.isPrivate);

export const selectedReportSpaceDefaultAccessSelector = createSelector(headerReportData, (headerData: any) => {
  return headerData?._embedded?.space?.default_access_level;
});

// @todo add to reducers
export const selectedReportSpaceWebLinkIsViewableSelector = (state: AppState): boolean => {
  const space = selectedReportSpaceSelector(state);
  return (
    space &&
    !SpaceHelpers.isRestrictedPersonalSpace(space) &&
    !SpaceHelpers.isPrivateAndNotCreatedBy(space, accountSelector(state))
  );
};

export const selectedReportSpaceNameSelector = createSelector(headerReportData, (headerData: any) => {
  return headerData?._embedded?.space?.name;
});

export const selectedReportSpaceDisplayNameSelector = createSelector(
  accountSelector,
  selectedReportCreatorSelector,
  selectedReportSpaceSelector,
  headerReportData,
  reportContextSelector,
  (user, creator, space, report, reportContext) => {
    if (report == null) {
      return null;
    }

    if (report.last_run_at == null) {
      return 'Personal';
    } else if (report.last_run_at != null && creator == null && !space) {
      // The user must have just run it, since we'd have a creator if we loaded an already ran report.
      // So just return the current user's name if the report doesn't belong a collection.
      // user may be null at times, but it's ok to return blank at those times, since it'll load later
      return user?.name;
    }
    return SpaceHelpers.resolveDisplayName(space, creator, report, reportContext);
  }
);

export const definitionActionsSelector = (state: AppState): ClientState['definitionActions'] =>
  state.client.definitionActions;

export const definitionActionsIndexByIdSelector = createSelector(definitionActionsSelector, (actions) =>
  ImmMap(mapKeys(actions, (action) => action.get('id')))
);

export const selectedTableSelector = createSelector(
  (state: AppState) => state.reportEditor.selectedQueryToken,
  (state: AppState) => state.client.selectedReport.tables,
  // This searches by query token because there's only one display table for a query, and we
  // don't want to store a legacy table token in the state
  (queryToken, tables) => tables.getRecords().find((t) => t.queryToken === queryToken)
);

// TODO: stub left to keep the boiler plate in place for the color mapping state in the embed for upcoming colors project
export const reportLevelColorMaps = createSelector(selectedReportResourceSelector, (report) =>
  report ? report.color_mappings : {}
);

// Router
export const routerSelector = (state: AppState) => state.router;

export const routerNameSelector = (state: AppState) => state.router.name;

export const routerParamsSelector = (state: AppState) => state.router.params;

// Transient
export const transientReportSelector = (state: AppState) => state.transient.report;

export const reportModelsSelector = (state: AppState) => state.client.selectedReport.chartModels;

// Returns only model states from successful queries
export const validReportModelsSelector = (state: AppState) =>
  state.client.selectedReport.chartModels.getRecords().filter((m) => m.model != null);

export const reportFilterControlsSelector = createSelector(
  (state: AppState) => state.reportFilter.tokens,
  (state: AppState) => state.reportFilter.filters,
  (state: AppState) => state.reportFilter.selections,
  (state: AppState) => state.reportFilter.defaults,
  (tokens, filters, selections, defaults) =>
    tokens.map((token) =>
      ReportFilterControlHelpers.convertToFilterControl(filters[token], selections[token], defaults[token])
    )
);

/*
  Only used in insert-filter-modal,
*/

export const reportFilterFieldsSelectorWithDatasets = createSelector(
  fromReportFilterSelectors.selectFilterDefinitionsArray,
  fromSwitchViewSelectors.getModel,
  (controls, model) => {
    const filterControls = controls;
    const filterFields = new Array<ReportFilterFieldTypes>();
    const nameMap = new Map<string, Set<string>>();

    model.views.forEach((view) => {
      for (const attr of view.attrs) {
        let dataTypes = nameMap.get(attr.name);

        if (dataTypes === undefined) {
          dataTypes = new Set<string>();
          nameMap.set(attr.name, dataTypes);
        }

        if (!dataTypes.has(attr.formula.type.name)) {
          const compiled = CompiledAttr.fromAttr(view, attr);
          // we currently only support report filters for Scalar fields
          if (compiled.isValid) {
            if (!compiled.isNonScalarCalc) {
              switch (attr.formula.type.name) {
                case DataType.Name.DateTime:
                case DataType.Name.Timestamp:
                  filterFields.push(ReportFilterFieldFactory.buildDefaultDateFilterFolder(compiled, filterControls));
                  break;
                case DataType.Name.Decimal:
                case DataType.Name.Float:
                case DataType.Name.Integer:
                  filterFields.push(ReportFilterFieldFactory.buildDefaultRangeFilterField(compiled, filterControls));
                  break;
                default:
                  filterFields.push(ReportFilterFieldFactory.buildDefaultSelectFilterField(compiled, filterControls));
                  break;
              }

              dataTypes.add(attr.formula.type.name);
            } else {
              // this is a placeholder filter list item for display purposes
              filterFields.push(ReportFilterFieldFactory.buildDefaultRangeFilterField(compiled, filterControls, true));
            }
          }
        }
      }
    });

    return sortBy(filterFields, (filterField) => filterField.name.toLocaleLowerCase());
  }
);

export const reportResourcesByKeySelector = (state: AppState): ImmMap<string, ReportResource> =>
  state.persistent.reports.byKey;

export const reportFilterResourcesByKeySelector = (state: AppState): ImmMap<string, ReportTypes.ReportFilterResource> =>
  state.persistent.reportFilters.byKey;

export const queryResourcesByKeySelector = (state: AppState): ImmMap<string, QueryState> =>
  state.client.selectedReport.queries.byKey;

export const transientSelectedQueryTokenSelector = (state: AppState): string => state.reportEditor.selectedQueryToken;

export const selectedQueryCursorPositionsSelector = createSelector(
  transientSelectedQueryTokenSelector,
  (state: AppState) => state.transient.editor.cursorPositions.get(state.reportEditor.selectedQueryToken),
  (selectedQueryToken, cursorPosition) => {
    const { selectionLead, selectionAnchor } = cursorPosition || { selectionLead: 0, selectionAnchor: 0 };
    const selectionStart = Math.min(selectionLead, selectionAnchor);
    const selectionEnd = Math.max(selectionLead, selectionAnchor);
    return { selectionLead, selectionAnchor, selectionStart, selectionEnd, selectedQueryToken };
  }
);

export const selectedQuerySelector = createSelector(
  transientSelectedQueryTokenSelector,
  queryResourcesByKeySelector,
  (queryToken, queries) => {
    return queries.get(queryToken);
  }
);

// data_source_id is really a number, but historically was typed incorrectly as a string
export const selectedQueryOrDefinitionDatasourceId = createSelector(
  selectedQuerySelector,
  selectedDefinitionQueryResource,
  (query, definition) => {
    return (query?.data_source_id || definition?.data_source_id) as unknown as number;
  }
);

export const editorStateSelector = createSelector(
  selectedQuerySelector,
  selectedQueryCursorPositionsSelector,
  (query, cursorPos) => ({ ...cursorPos, text: query ? query.raw_query : '' })
);

export const highlightedQueryTextSelector = createSelector(
  editorStateSelector,
  ({ text, selectionStart, selectionEnd }) => text?.slice(selectionStart, selectionEnd)
);

export const modifiedQueriesSelector = createSelector(SelectedReportSelectors.getQueries, (queries) => {
  return queries.getRecords().filter(
    (query) =>
      // if the query resource is null (probably only for a moment), don't want to say it's
      // modified because the report save side-effect will attempt to save it, which isn't
      // possible without a resource.
      query.resource != null &&
      (query.resource.name !== query.name ||
        query.resource.raw_query !== query.raw_query ||
        query.resource.data_source_id !== query.data_source_id)
  );
});

// Persistent
export const persistentSelector = (state: AppState) => state.persistent;

export const reportDatasetsSelector = (state: AppState): Readonly<ImmutableRecordSet<ReportDataset>> =>
  state.transient.report.datasets;

export const reportQueryRunSelector = (state: AppState): Readonly<ImmutableRecordSet<QueryRunState>> =>
  state.client.selectedReport.queryRuns;
